import {
  getStakeContract,
  getStakePatchContract,
  getReadWeb3,
  getAccountAddress,
} from "../index";
import { ZERO_ADDRESS, getCardLevelByTokenId } from "../constants";
import {
  DEFAULT_PARENT_ADDRESS,
  CARD_USER_PROFILE,
  calcCardBenefitPercent,
  calcCardBenefitRange,
  canExchangeAnyCard,
} from "@/views/pledge/config";
import {
  ensureUsdtAllowanceForStake,
} from "./tokenService";
import {
  assertWalletReady,
  formatTokenAmount,
  parseTokenAmount,
  sendContractMethod,
} from "./txHelper";

function logClaimTransactionHash(label, receipt) {
  const hash = receipt?.transactionHash;
  console.log(`[${label}] transactionHash:`, hash);
  return hash;
}

// const DAY_SECONDS = 86400;
const DAY_SECONDS = 30;
function calcRemainingDays(startTime, lockDays, now) {
  const maturityTime = Number(startTime) + Number(lockDays) * DAY_SECONDS;
  const remainingSeconds = maturityTime - now;
  if (remainingSeconds <= 0) {
    return 0;
  }
  return Math.ceil(remainingSeconds / DAY_SECONDS);
}

async function fetchStakeParams() {
  const stake = getStakeContract();
  const stakePatch = getStakePatchContract();
  const [minDepositAmount, daySeconds, dailyRate180, dailyRate360] =
    await Promise.all([
      stake.methods.minDepositAmount().call(),
      stakePatch.methods.daySeconds().call(),
      stakePatch.methods.dailyRate180().call(),
      stakePatch.methods.dailyRate360().call(),
    ]);
  return {
    minDepositAmount,
    daySeconds: Number(daySeconds),
    dailyRate180: Number(dailyRate180),
    dailyRate360: Number(dailyRate360),
  };
}

/** 未提取收益：从 getUserLastClaimTime 计到 min(now, 到期) */
async function calcPendingRewardRusd(userAddress, stakeInfo, index, params) {
  const now = Math.floor(Date.now() / 1000);
  const startTime = Number(stakeInfo.startTime);
  const lockDays = Number(stakeInfo.lockDays);
  const stakeAmount = BigInt(stakeInfo.stakeAmount);

  const stakePatch = getStakePatchContract();
  const lastRewardTime = Number(
    await stakePatch.methods.getUserLastClaimTime(userAddress, index).call()
  );

  const maturityTime = startTime + lockDays * params.daySeconds;
  if (lastRewardTime >= maturityTime) {
    return 0n;
  }

  const endTime = now > maturityTime ? maturityTime : now;
  const pendingTime = endTime - lastRewardTime;
  if (pendingTime <= 0) {
    return 0n;
  }

  const rate =
    lockDays === 180 ? params.dailyRate180 : params.dailyRate360;
  const pendingU =
    (stakeAmount * BigInt(rate) * BigInt(pendingTime)) /
    (BigInt(params.daySeconds) * 10000n);

  if (pendingU <= 0n) {
    return 0n;
  }

  const rusdAmount = await stakePatch.methods
    .getRusdAmount(String(pendingU))
    .call();
  return BigInt(rusdAmount);
}

export async function getMinDepositAmount() {
  const stake = getStakeContract();
  const min = await stake.methods.minDepositAmount().call();
  return formatTokenAmount(min);
}

export async function getParentAddress(userAddress) {
  const stake = getStakeContract();
  const parent = await stake.methods.getParent(userAddress).call();
  if (!parent || parent === ZERO_ADDRESS) {
    return "";
  }
  return parent;
}

function countUserCards(cardIds) {
  let goldCards = 0;
  let silverCards = 0;
  let bronzeCards = 0;

  cardIds.forEach((tokenId) => {
    const level = getCardLevelByTokenId(tokenId);
    if (level === "gold") goldCards += 1;
    if (level === "silver") silverCards += 1;
    if (level === "bronze") bronzeCards += 1;
  });

  return { goldCards, silverCards, bronzeCards };
}

function resolveCardUserProfile(counts) {
  const { goldCards, silverCards, bronzeCards } = counts;
  if (goldCards + silverCards + bronzeCards === 0) {
    return null;
  }
  const bonusRange = calcCardBenefitRange(goldCards, silverCards, bronzeCards);
  if (goldCards > 0) {
    return { level: "gold", ...CARD_USER_PROFILE.gold, bonusRange, ...counts };
  }
  if (silverCards > 0) {
    return { level: "silver", ...CARD_USER_PROFILE.silver, bonusRange, ...counts };
  }
  return { level: "bronze", ...CARD_USER_PROFILE.bronze, bonusRange, ...counts };
}

export async function fetchUserCardProfile(userAddress) {
  if (!userAddress) {
    return null;
  }
  const stakePatch = getStakePatchContract();
  const cardIds = await stakePatch.methods.getUserCards(userAddress).call();
  return resolveCardUserProfile(countUserCards(cardIds));
}

export async function fetchStakingRecords(userAddress) {
  const stake = getStakeContract();
  const params = await fetchStakeParams();
  const stakes = await stake.methods.getUserStakes(userAddress).call();
  const now = Math.floor(Date.now() / 1000);

  const records = await Promise.all(
    stakes
      .map((item, index) => ({ item, index }))
      .filter(({ item }) => !item.isPrincipalClaimed)
      .map(async ({ item, index }) => {
        const lockDays = Number(item.lockDays);
        const remainingDays = calcRemainingDays(item.startTime, lockDays, now);
        const pendingRusd = await calcPendingRewardRusd(
          userAddress,
          item,
          index,
          params
        );

        return {
          id: index + 1,
          index,
          remainingTime: `${remainingDays}天`,
          remainingDays,
          type: `${lockDays}天`,
          amount: formatTokenAmount(item.stakeAmount),
          lpAmount: formatTokenAmount(item.lpAmount),
          reward: formatTokenAmount(String(pendingRusd)),
          canRedeem: remainingDays === 0,
          hasPendingReward: pendingRusd > 0n,
        };
      })
  );

  return records;
}

export async function fetchPersonalInfo(userAddress) {
  const stake = getStakeContract();
  const stakePatch = getStakePatchContract();
  const [
    userInfo,
    claimedReward,
    cardIds,
    directPoints,
    teamPoints,
  ] = await Promise.all([
    stake.methods.getUserInfo(userAddress).call(),
    stakePatch.methods.getClaimedReward(userAddress).call(),
    stakePatch.methods.getUserCards(userAddress).call(),
    stake.methods.directPoints(userAddress).call(),
    stake.methods.teamPoints(userAddress).call(),
  ]);
  // StakePatch.getUserInfo: [卡牌收益, 推广收益]
  const patchUserInfo = await stakePatch.methods
    .getUserInfo(userAddress, cardIds)
    .call();
  const pendingCardReward = patchUserInfo[0];
  const pendingShareReward = patchUserInfo[1];

  let goldCards = 0;
  let silverCards = 0;
  let bronzeCards = 0;
  const counts = countUserCards(cardIds);
  goldCards = counts.goldCards;
  silverCards = counts.silverCards;
  bronzeCards = counts.bronzeCards;
  const totalCards = goldCards + silverCards + bronzeCards;

  return {
    goldCards,
    silverCards,
    bronzeCards,
    totalCards,
    cardBenefit: calcCardBenefitPercent(goldCards, silverCards, bronzeCards),
    canExchangeCard: canExchangeAnyCard(directPoints, teamPoints),
    directPoints: formatTokenAmount(directPoints, 18),
    teamPoints: formatTokenAmount(teamPoints, 18),
    directPointsRaw: directPoints,
    teamPointsRaw: teamPoints,
    cardReward: `${formatTokenAmount(pendingCardReward)} RUSD`,
    directReferrals: Number(userInfo[1]),
    directPerformance: `${formatTokenAmount(userInfo[2])} USDT`,
    totalPerformance: `${formatTokenAmount(userInfo[3])} USDT`,
    promotionReward: `${formatTokenAmount(pendingShareReward)} RUSD`,
    withdrawnStaking: `${formatTokenAmount(claimedReward[0])} RUSD`,
    withdrawnPromotion: `${formatTokenAmount(claimedReward[1])} RUSD`,
    withdrawnCard: `${formatTokenAmount(claimedReward[2])} RUSD`,
    pendingPromotionReward: BigInt(pendingShareReward),
    pendingCardReward: BigInt(pendingCardReward),
  };
}

export async function stakeToken(amount, lockDays, parentAddress) {
  assertWalletReady();
  const amountWei = parseTokenAmount(amount);
  await ensureUsdtAllowanceForStake(amountWei);

  const stake = getStakeContract();
  const parent = parentAddress || ZERO_ADDRESS;
  const method = stake.methods.stakeToken(amountWei, lockDays, parent);
  return sendContractMethod(method);
}

export async function claimPrincipal(index) {
  assertWalletReady();
  const stake = getStakeContract();
  const method = stake.methods.claimPrincipal(index);
  return sendContractMethod(method);
}

export async function claimAllStakeRewards(userAddress) {
  assertWalletReady();
  const records = await fetchStakingRecords(userAddress);
  const pendingRecords = records.filter((item) => item.hasPendingReward);
  console.log(pendingRecords,'pendingRecords');
  if (pendingRecords.length === 0) {
    throw new Error("暂无可提取的质押收益");
  }

  const stakePatch = getStakePatchContract();
  for (const record of pendingRecords) {
    const method = stakePatch.methods.claimStakeReward(record.index);
    const receipt = await sendContractMethod(method);
    console.log(receipt);
    logClaimTransactionHash("提取质押收益", receipt);
  }
}

export async function claimShareReward() {
  assertWalletReady();
  const stakePatch = getStakePatchContract();
  const method = stakePatch.methods.claimShareReward();
  const receipt = await sendContractMethod(method);
  logClaimTransactionHash("提取推广权益", receipt);
  return receipt;
}

export async function claimCardsReward() {
  assertWalletReady();
  const userAddress = getAccountAddress();
  const stakePatch = getStakePatchContract();
  const tokenIds = await stakePatch.methods.getUserCards(userAddress).call();
  if (!tokenIds || tokenIds.length === 0) {
    throw new Error("暂无可提取的卡牌权益");
  }

  const method = stakePatch.methods.claimCardsReward(tokenIds);
  const receipt = await sendContractMethod(method);
  logClaimTransactionHash("提取卡牌权益", receipt);
  return receipt;
}

export async function exchangeCard(cardType) {
  assertWalletReady();
  const stake = getStakeContract();
  const method = stake.methods.exchangeCard(cardType);
  const receipt = await sendContractMethod(method);
  logClaimTransactionHash("积分兑换卡牌", receipt);
  return receipt;
}

export function getParentFromUrl() {
  const params = new URLSearchParams(window.location.search);
  const parent = params.get("parent");
  if (!parent || !getReadWeb3().utils.isAddress(parent)) {
    return "";
  }
  return getReadWeb3().utils.toChecksumAddress(parent);
}

/** @deprecated 使用 getParentFromUrl */
export function getInviteParentFromUrl() {
  return getParentFromUrl();
}

export function resolveStakeParentAddress(boundParent) {
  if (boundParent && boundParent !== ZERO_ADDRESS) {
    return boundParent;
  }
  return getParentFromUrl() || DEFAULT_PARENT_ADDRESS;
}
